import {
  ConfirmationModal,
  Page,
  useModals,
  Button,
  ButtonLink,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { BlogPost } from "@/pages/blog/components/BlogPost.tsx";
import { BlogAddComment } from "@/pages/blog/components/BlogAddComment.tsx";
import { BlogPostComment } from "@/pages/blog/components/BlogPostComment.tsx";
import { BlogPostSkeleton } from "@/pages/blog/components/BlogPostSkeleton.tsx";
import { useBlogAttachmentDeepLink } from "@/pages/blog/use-blog.hook.ts";
import {
  useBlogAddComment,
  useBlogCommentsSearch,
  useBlogPostDelete,
  useBlogPostDetails,
  useBlogReadPost,
} from "@/queries/blog.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiActionPayload } from "@/types/action.types.ts";
import { ApiComment } from "@/types/comment.types.ts";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useEffectOnce } from "react-use";

export function BlogPostPage() {
  const { post_id } = useParams();
  const postId = Number(post_id);
  const { openModal } = useModals();
  const { data: post, isLoading: isPostLoading } = useBlogPostDetails(postId);
  useBlogAttachmentDeepLink(post?.id, post?.files);
  const { mutateAsync: deleteAction, isPending: isDeleting } = useBlogPostDelete();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutateAsync: saveComment } = useBlogAddComment();
  const { mutateAsync: readPost } = useBlogReadPost();
  const {
    data: comments,
    isLoading: isLoadingComments,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useBlogCommentsSearch({
    page_number: 1,
    post_id: Number(post_id),
  });

  const { channel_id } = useParams();
  const channelId = channel_id ? Number(channel_id) : undefined;
  const currentChannels = useMemo(() => {
    if (channelId) return post?.channels.find((channel) => channel.id === channelId);
    if (post?.channels && post?.channels.length > 0) return post.channels[0];
  }, [channelId, post]);

  const title = `${t("blog:news_and_updates")}  ${currentChannels ? `·  ${currentChannels.title}` : ""}`;

  useTitle(
    post ? post.title : undefined,
    currentChannels ? currentChannels.title : undefined,
    t("blog:blog")
  );

  useEffectOnce(() => {
    readPost({ id: Number(post_id) }).then();

    analyticsService.trackEvent(analyticEvents.blog.postViewed, {
      [analyticProperties.id]: post_id,
    });
  });

  const flatComments = useMemo(
    () => comments?.pages?.flatMap((page) => page?.items ?? []) ?? [],
    [comments]
  );

  const countComments = useMemo(() => {
    return comments?.pages?.at(-1)?.metadata.total_items ?? undefined;
  }, [comments]);

  const addComment = async (payload: ApiActionPayload) => {
    if (!post) return false;

    const result = await saveComment({
      postId: post.id,
      text: payload.comment ?? "",
      fileUuids: payload.file_uuids,
    });

    analyticsService.trackEvent(analyticEvents.blog.commentCreated);

    return !!result.comment;
  };

  const deletePost = async () => {
    if (!post) return false;

    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("blog:delete_post_question"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        const result = await deleteAction(post.id);

        if (result?.success) {
          analyticsService.trackEvent(analyticEvents.blog.postDeleted, {
            [analyticProperties.id]: post.id,
          });

          navigate("/news");
        }
      },
    });
  };

  const isScheduled = useMemo(() => {
    return post?.datetime_published_at
      ? post?.datetime_published_at.getTime() > new Date().getTime()
      : false;
  }, [post]);

  return (
    <Page variant="opaque">
      <Page.Header
        title={title}
        showBack={true}
        navigateTo={channelId ? `/news/${channelId}` : "/news"}
      >
        <Stack gap={"sm"} direction={"horizontal"}>
          {post?.acl?.can_edit && (
            <ButtonLink href={`/news/posts/${post?.id}/edit`}>{t("common:edit")}</ButtonLink>
          )}
          {post?.acl?.can_delete && (
            <Button variant={"danger"} onClick={deletePost} isLoading={isDeleting}>
              {t("common:delete")}
            </Button>
          )}
        </Stack>
      </Page.Header>
      <Page.Content className={"py-6 px-16"}>
        <Stack className={"container-sm ml-auto mr-auto"} gap={"lg"}>
          {isPostLoading && <BlogPostSkeleton />}
          {post && <BlogPost mode={"long"} post={post} />}
          {post && !isScheduled ? (
            post.comments_disabled ? (
              <Text className={"text-secondary "}>{t("blog:comments_disabled")}</Text>
            ) : (
              <Stack>
                <Title
                  header={`${t("common:comments")} ${!isLoadingComments && countComments ? `(${countComments})` : ""}`}
                  paddingBottom={true}
                  size={"lg"}
                />
                <Stack gap={"lg"} className={"bg-light rounded-sm shadow p-6"}>
                  <BlogAddComment
                    postId={post.id}
                    save={addComment}
                    label={`${t("common:add_comment")}...`}
                    submitLabel={t("core:send")}
                  />
                  {isLoadingComments ||
                    (isFetchingNextPage && (
                      <Stack items={"start"} gap={"sm"}>
                        <Stack direction={"horizontal"} gap={"sm"}>
                          <Skeleton className={"h-5 w-5"} />
                          <Skeleton className={"h-5 w-24"} />
                        </Stack>

                        <Skeleton className={"h-5 w-32"} />
                      </Stack>
                    ))}
                  {flatComments.map((comment: ApiComment, index) => (
                    <BlogPostComment
                      comment={comment}
                      key={`comment-${comment.entity_id}-${index}`}
                      postId={postId}
                      mode={"long"}
                    />
                  ))}
                  {hasNextPage && (
                    <Stack direction={"horizontal"}>
                      <Button
                        size={"sm"}
                        isLoading={isFetchingNextPage}
                        onClick={async () => {
                          await fetchNextPage();
                        }}
                      >
                        {t("common:show_more")}
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            )
          ) : (
            <></>
          )}
        </Stack>
      </Page.Content>
    </Page>
  );
}
